import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import type { ButtonShape } from "../atoms/Button/button-variants.js";
import { chromeControlClass } from "../lib/chrome-control.js";
import { cn } from "../lib/utils.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";
import { useWorkspaceControlDensity } from "./workspace-control-density.js";

type WorkspaceControlButtonBase = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label" | "aria-pressed" | "children"
> & {
  /** The glyph. The label names the button, so the icon adds no name. */
  readonly icon: ReactNode;
  /** The accessible name at every density, and the visible text unless
   *  `iconOnly` or collapsed. */
  readonly label: string;
  /** Icon before the label (default) or after it (an Advance). */
  readonly iconPosition?: "start" | "end" | undefined;
  /** The label collapses while the group hides labels. Default `true`.
   *  `false` keeps it visible at every density. */
  readonly collapseLabel?: boolean | undefined;
  /** Shown only while the label is hidden. Default the label; `false` for
   *  none. A tooltip other than the label also describes the button. */
  readonly tooltip?: ReactNode | false | undefined;
  /**
   * Show the tooltip on hover and keyboard focus at EVERY density, not only
   * while the label is hidden — for a toolbar whose controls must name
   * themselves the same way however much room the group has. Default `false`.
   */
  readonly alwaysShowTooltip?: boolean | undefined;
};

/* pressed is typed to the ghost variant: a primary is the region's commit,
   never a toggle. */
type VariantProps =
  | {
      /** `ghost` (default) is the rail look; `primary` is the region's one
       *  commit (invariant 9): Button primary's paint on the 32px box. */
      readonly variant?: "ghost" | undefined;
      /** Sets aria-pressed and paints the teal selection. */
      readonly pressed?: boolean | undefined;
    }
  | {
      readonly variant: "primary";
      readonly pressed?: never;
      /* The caption's muted text is not legible on the accent fill. */
      readonly caption?: never;
    };

/* circle is the square box, so it needs iconOnly (Button's rule). A caption
   needs the label's text, so the square box takes none. */
type ShapeProps =
  | {
      /** The 32px square: no visible label, the tooltip always on. */
      readonly iconOnly: true;
      /** Corner (C7). Default `pill`; pill and circle are the 32px circle. */
      readonly shape?: ButtonShape | undefined;
      readonly caption?: never;
    }
  | {
      readonly iconOnly?: false | undefined;
      /** Corner (C7). Default `pill`, today's rounded-full. */
      readonly shape?: Exclude<ButtonShape, "circle"> | undefined;
      /**
       * A second, smaller line under the label (muted, one line, truncates).
       * It is the button's accessible description; the name stays `label`.
       * The box grows to fit both lines and the label never collapses.
       */
      readonly caption?: ReactNode;
    };

/* A caption's box: it grows past 32px to fit two lines. */
const CAPTION_BOX = "h-auto min-h-8 py-0.5";

export type WorkspaceControlButtonProps = WorkspaceControlButtonBase &
  VariantProps &
  ShapeProps;

/**
 * A 32px control for a `WorkspaceControlGroup` rail: an icon and a label that
 * collapses when the group hides labels. It paints from the internal
 * chrome-control recipe (`lib/chrome-control.ts`): `ghost` is the rail look
 * (neutral hover, teal only when `pressed`), `primary` is Button primary's
 * paint on the same box. 32px stays off the public Button size ramp.
 *
 * The accessible name is always `label`, so a collapsed group stays
 * addressable by the same name. The label collapses through the group's
 * `data-labels` (the `pb-workspace-control-label` utility), and the tooltip
 * shows only while the label is hidden. `ref` and every other prop reach the
 * `<button>`, so it works as a `PopoverTrigger` or `Tooltip` child.
 *
 * `caption` puts a second, smaller line under the label (a phase title with
 * "Room: Current state · Live" under it). The two lines stack, start-aligned,
 * with the icon centred beside them; the box grows from 32px to fit them. The
 * caption is the button's description (merged with the caller's
 * `aria-describedby`), so the name stays `label`. The label never collapses
 * and truncates inside the caller's max-width. Without `caption` nothing
 * changes.
 */
export const WorkspaceControlButton = forwardRef<
  HTMLButtonElement,
  WorkspaceControlButtonProps
>(function WorkspaceControlButton(props, ref): ReactElement {
  const {
    icon,
    label,
    iconOnly = false,
    variant = "ghost",
    pressed,
    shape = "pill",
    iconPosition = "start",
    collapseLabel = true,
    tooltip,
    alwaysShowTooltip = false,
    caption,
    className,
    type,
    ...rest
  } = props as WorkspaceControlButtonBase & {
    iconOnly?: boolean | undefined;
    variant?: "ghost" | "primary" | undefined;
    pressed?: boolean | undefined;
    shape?: ButtonShape | undefined;
    caption?: ReactNode;
  };
  const { labels } = useWorkspaceControlDensity();
  const captionId = useId();
  const captioned = caption !== undefined;
  const square = iconOnly || shape === "circle";
  const collapsed =
    !square && collapseLabel && !captioned && labels === "hidden";
  const describedBy = captioned
    ? [captionId, rest["aria-describedby"]].filter(Boolean).join(" ")
    : rest["aria-describedby"];

  const text = square ? null : captioned ? (
    <span className="flex min-w-0 flex-col text-start">
      <span className="truncate">{label}</span>
      <span
        id={captionId}
        className="truncate text-dense-2 font-normal text-pb-text-muted"
      >
        {caption}
      </span>
    </span>
  ) : (
    <span
      className={
        collapseLabel ? "pb-workspace-control-label" : "whitespace-nowrap"
      }
    >
      {label}
    </span>
  );

  const button = (
    // eslint-disable-next-line ds/no-hand-built -- this is the design-system part: it paints from lib/chrome-control.ts, and 32px is not a Button size
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(
        chromeControlClass({
          look: variant === "primary" ? "primary" : "rail",
          shape,
          iconOnly: square,
          collapsed,
          pressed,
        }),
        captioned && CAPTION_BOX,
        className,
      )}
      {...rest}
      {...(captioned ? { "aria-describedby": describedBy } : {})}
    >
      {iconPosition === "end" ? text : null}
      {icon}
      {iconPosition === "end" ? null : text}
    </button>
  );

  if (tooltip === false) return button;
  return (
    <Tooltip
      content={tooltip ?? label}
      disabled={!alwaysShowTooltip && !square && !collapsed}
      // The label tooltip repeats the name, so only another text describes.
      describeChild={tooltip !== undefined && describedBy === undefined}
    >
      {button}
    </Tooltip>
  );
});
